import authService from "../api/auth/authService";
import tokenStore from "./tokenStore";
import { postSessionMessage } from "./sessionChannel";
import Cookies from "js-cookie";

// The one logout ritual (extracted from the header dropdown so the session
// modal, the interceptor and other tabs all leave the app the same way).
// reason 'expired' → the login page shows "Your session has expired..."
// instead of a silent redirect.

export const SESSION_EXPIRED_MESSAGE_KEY = "session-expired-message";
export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please sign in again.";

async function clearClientState(): Promise<void> {
  tokenStore.clearAll();
  Cookies.remove("currentProjectId");
  Cookies.remove("currentProjectName");

  // FCM token — best-effort (firebase may not be initialised on this page)
  try {
    const fcmToken = sessionStorage.getItem("fcmToken");
    if (fcmToken) {
      const { getMessaging, deleteToken } = await import("firebase/messaging");
      await deleteToken(getMessaging());
      sessionStorage.removeItem("fcmToken");
    }
  } catch {}

  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) await registration.unregister();
    }
  } catch {}

  localStorage.clear();
  sessionStorage.clear();

  try {
    if ("caches" in window) {
      const keyList = await caches.keys();
      await Promise.all(keyList.map((key) => caches.delete(key)));
    }
  } catch {}

  try {
    if (window.indexedDB && indexedDB.databases) {
      const databases = await indexedDB.databases();
      databases.forEach((db: any) => db?.name && indexedDB.deleteDatabase(db.name));
    }
  } catch {}
}

// Full sign-out initiated in THIS tab: revoke the session server-side,
// broadcast to sibling tabs, clear everything, land on the login page.
export async function performSignOut(reason: "user" | "expired"): Promise<void> {
  postSessionMessage({ type: "LOGOUT", reason });
  try {
    // Revokes THIS session's refresh token (works even with an expired access
    // token — the backend logout guard ignores expiry).
    await authService.logout();
  } catch {}
  await clearClientState();
  if (reason === "expired") {
    try {
      sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, SESSION_EXPIRED_MESSAGE);
    } catch {}
  }
  window.location.href = "/";
}

// A sibling tab already revoked the session — just clear and leave, no
// API call and no re-broadcast (avoids loops).
export async function performLocalSignOut(reason: "user" | "expired"): Promise<void> {
  await clearClientState();
  if (reason === "expired") {
    try {
      sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, SESSION_EXPIRED_MESSAGE);
    } catch {}
  }
  window.location.href = "/";
}
